import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import {
  AnyAccountJwtVO,
  RoomEntity,
  StreamId,
  StreamIdentity,
  StreamIsActive,
  StreamKind,
  StreamSpecs
} from "@common/domain";
import {
  Actions,
  concatLatestFrom,
  createEffect,
  EffectNotification,
  ofType,
  OnRunEffects
} from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { createEmptyCamMicStream } from "@youm-front/account-cammic/util/factory";
import { AccountJwtService } from "@youm-front/account-jwt/data-access";
import {
  LocalConferenceStreamActions,
  RemoteConferenceStreamActions
} from "@youm-front/conference-stream/data-access";
import { DeviceService } from "@youm-front/device/data-access";
import { LokiActions } from "@youm-front/loki/data-access";
import { RoomStreamDeleteCmd, RoomStreamVO } from "@youm-front/room/domain";
import { combineLatest, EMPTY, Observable } from "rxjs";
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  map,
  mergeMap,
  take,
  takeUntil,
  tap,
  timeout
} from "rxjs/operators";
import { PlainExtendedMediaStream } from "youm-lib";
import {
  errorLogSummary,
  LocalAnyConferenceStream,
  LocalAudioConferenceStream,
  LocalCamMicConferenceStream,
  LocalScreenConferenceStream,
  LocalVideoConferenceStream
} from "youm-lib/conference";
import {
  RoomActions,
  RoomProducerActions,
  RoomStreamActions
} from "../actions";
import {
  RoomStreamDeleteRequestDTO,
  RoomStreamToPublishCreateRequestDTO
} from "../dto";
import { RoomContextFacade } from "../facades";
import { RoomSelectors } from "../selectors";
import { RoomService } from "../services";

@Injectable()
export class RoomProducerPublishEffects implements OnRunEffects {
  readonly streams$ = this.store.pipe(select(RoomSelectors.roomStreams));
  readonly room$ = this.store.pipe(select(RoomSelectors.room));

  constructor(
    private readonly accountJwtService: AccountJwtService,
    private readonly actions$: Actions,
    private readonly deviceService: DeviceService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly roomContextFacade: RoomContextFacade,
    private readonly roomService: RoomService,
    private readonly store: Store
  ) {}

  publishCamMicStream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.publishWebcamStream),
      concatMap(() =>
        this.accountRoom$.pipe(
          take(1),
          map(({ accountJwt, room }) => ({
            accountJwt,
            room,
            stream: new LocalCamMicConferenceStream(
              StreamSpecs.ofType(
                accountJwt.value.id as unknown as StreamIdentity,
                "camMic",
                this.roomContextFacade.roomContextId
              ),
              createEmptyCamMicStream(),
              (...args: unknown[]) =>
                this.store.dispatch(
                  LokiActions.log({
                    args: [
                      ...args,
                      { roomContextId: this.roomContextFacade.roomContextId }
                    ]
                  })
                )
            )
          }))
        )
      ),
      concatMap(({ accountJwt, room, stream }) =>
        this.publishStream({ accountJwt, room, stream }).pipe(
          timeout(15_000),
          tap((_) =>
            LocalConferenceStreamActions.streamAdd({
              stream,
              kinds: _.data.streamKinds,
              credentials: _.data.credentials
            })
          ),
          mergeMap((_) => [
            RoomActions.publishWebcamStreamSuccess({
              roomStreamVO: new RoomStreamVO(_.data)
            })
          ]),
          catchError((_: Error) => [
            RoomActions.publishWebcamStreamFailure({
              error: _.message
            })
          ])
        )
      )
    )
  );

  publishLocalAudioFileStream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.publishLocalAudioStream),
      concatMap(({ file }) =>
        this.accountRoom$.pipe(
          take(1),
          map(({ accountJwt, room }) => ({
            accountJwt,
            room,
            stream: new LocalAudioConferenceStream(
              StreamSpecs.ofType(
                accountJwt.value.id as unknown as StreamIdentity,
                "localAudioFile",
                this.roomContextFacade.roomContextId
              ),
              file,
              () => this.document.createElement("audio"),
              (...args: unknown[]) =>
                this.store.dispatch(
                  LokiActions.log({
                    args: [
                      ...args,
                      { roomContextId: this.roomContextFacade.roomContextId }
                    ]
                  })
                )
            )
          }))
        )
      ),
      concatMap(({ accountJwt, room, stream }) =>
        this.publishStream({ accountJwt, room, stream }).pipe(
          timeout(15_000),
          tap((_) =>
            LocalConferenceStreamActions.streamAdd({
              stream,
              kinds: _.data.streamKinds,
              credentials: _.data.credentials
            })
          ),
          mergeMap((_) => [
            RoomActions.publishLocalAudioStreamSuccess({
              roomStreamVO: new RoomStreamVO(_.data)
            })
          ]),
          catchError((_: Error) => [
            RoomActions.publishLocalAudioStreamFailure({
              error: _.message
            })
          ])
        )
      )
    )
  );

  publishLocalVideoFileStream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.publishLocalVideoStream),
      concatMap(({ file }) =>
        this.accountRoom$.pipe(
          take(1),
          map(({ accountJwt, room }) => ({
            accountJwt,
            room,
            stream: new LocalVideoConferenceStream(
              StreamSpecs.ofType(
                accountJwt.value.id as unknown as StreamIdentity,
                "localVideoFile",
                this.roomContextFacade.roomContextId
              ),
              file,
              () => this.document.createElement("video"),
              () => this.document.createElement("canvas"),
              (...args: unknown[]) =>
                this.store.dispatch(
                  LokiActions.log({
                    args: [
                      ...args,
                      { roomContextId: this.roomContextFacade.roomContextId }
                    ]
                  })
                )
            )
          }))
        )
      ),
      concatMap(({ accountJwt, room, stream }) =>
        this.publishStream({ accountJwt, room, stream }).pipe(
          timeout(15_000),
          tap((_) =>
            LocalConferenceStreamActions.streamAdd({
              stream,
              kinds: _.data.streamKinds,
              credentials: _.data.credentials
            })
          ),
          mergeMap((_) => [
            RoomActions.publishLocalVideoStreamSuccess({
              roomStreamVO: new RoomStreamVO(_.data)
            })
          ]),
          catchError((_: Error) => [
            RoomActions.publishLocalVideoStreamFailure({
              error: _.message
            })
          ])
        )
      )
    )
  );

  publishScreenStream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.publishScreenStream),
      concatMap(() =>
        combineLatest([
          this.accountRoom$,
          this.deviceService.getDisplayMedia$().pipe(
            map((_) => new PlainExtendedMediaStream(_)),
            catchError(() => EMPTY)
          )
        ]).pipe(
          take(1),
          map((_) => ({
            accountJwt: _[0].accountJwt,
            room: _[0].room,
            stream: new LocalScreenConferenceStream(
              StreamSpecs.ofType(
                _[0].accountJwt.value.id as unknown as StreamIdentity,
                "screen",
                this.roomContextFacade.roomContextId
              ),
              _[1],
              (...args: unknown[]) =>
                this.store.dispatch(
                  LokiActions.log({
                    args: [
                      ...args,
                      { roomContextId: this.roomContextFacade.roomContextId }
                    ]
                  })
                )
            )
          })),
          tap((_) => {
            _.stream.setEndHandler(() =>
              this.store.dispatch(
                RoomStreamActions.roomStreamDelete({
                  roomStreamDeleteCmd: new RoomStreamDeleteCmd({
                    roomId: _.room.value.id,
                    streamId: _.stream.streamId
                  })
                })
              )
            );
          })
        )
      ),
      concatMap(({ accountJwt, room, stream }) =>
        this.publishStream({
          accountJwt,
          room,
          stream
        }).pipe(
          timeout(15_000),
          // The registration failed or timed out: the capture is never
          // published, stop it (no sharing indicator left, no orphan on retry).
          tap({
            error: () =>
              stream.destroy().catch((error: Error) =>
                this.store.dispatch(
                  LokiActions.log({
                    args: [
                      "screen-share.destroy.failure",
                      {
                        streamId: stream.streamId,
                        error: error?.message,
                        roomContextId: this.roomContextFacade.roomContextId
                      }
                    ]
                  })
                )
              )
          }),
          tap((_) => {
            LocalConferenceStreamActions.streamAdd({
              stream,
              kinds: _.data.streamKinds,
              credentials: _.data.credentials
            });
          }),
          mergeMap((_) => [
            RoomActions.publishScreenStreamSuccess({
              roomStreamVO: new RoomStreamVO(_.data)
            })
          ]),
          catchError((_: Error) => [
            RoomActions.publishScreenStreamFailure({
              error: _.message
            }),
            // YOUM-253: the room stream registration failed, so the media
            // publish (and its screen-share.publish.* logs) never starts.
            LokiActions.log({
              args: [
                "screen-share.request.failure",
                {
                  streamId: stream.streamId,
                  ...errorLogSummary(_)
                },
                { roomContextId: this.roomContextFacade.roomContextId }
              ]
            })
          ])
        )
      )
    )
  );

  // The screen 'Room Stream' is created before its media is published:
  // when the publish fails (or times out), delete it, otherwise its tile
  // stays empty and the next toggle only deletes it.
  screenStreamAddFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocalConferenceStreamActions.streamAddFailure),
      filter((_) => StreamSpecs.isOfType(_.id, "screen")),
      concatLatestFrom(() => this.room$),
      mergeMap(([{ id, error }, room]) => [
        LokiActions.log({
          args: [
            "screen-share.publish.failure",
            {
              streamId: id,
              error,
              roomContextId: this.roomContextFacade.roomContextId
            }
          ]
        }),
        RoomActions.publishScreenStreamFailure({ error }),
        ...(room
          ? [
              RoomStreamActions.roomStreamDelete({
                roomStreamDeleteCmd: new RoomStreamDeleteCmd({
                  roomId: room.value.id,
                  streamId: id
                })
              })
            ]
          : [])
      ])
    )
  );

  toggleScreenStream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.toggleScreenStream),
      concatLatestFrom(() => this.accountJwtService.anyAccountJwtVO$),
      concatMap(([, anyAccountJwt]) =>
        this.streams$.pipe(
          take(1),
          map((_) =>
            _.find(
              (_) =>
                anyAccountJwt?.value.id === _.value.accountId &&
                this.roomContextFacade.roomContextId ===
                  _.value.roomContextId &&
                StreamSpecs.isOfType(_.value.streamId, "screen")
            )
          )
        )
      ),
      concatMap((_) =>
        _
          ? [
              RoomStreamActions.roomStreamDelete({
                roomStreamDeleteCmd: _
              })
            ]
          : [RoomActions.publishScreenStream()]
      )
    )
  );

  roomStreamDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomStreamActions.roomStreamDelete),
      concatMap((_) =>
        this.roomService
          .roomStreamDelete(
            new RoomStreamDeleteRequestDTO({
              roomId: _.roomStreamDeleteCmd.value.roomId,
              streamId: _.roomStreamDeleteCmd.value.streamId
            })
          )
          .pipe(
            mergeMap(() => [
              RoomStreamActions.roomStreamDeleteSuccess({
                roomStreamDeleteCmd: _.roomStreamDeleteCmd
              }),
              LocalConferenceStreamActions.streamDelete({
                id: _.roomStreamDeleteCmd.value.streamId
              }),
              RemoteConferenceStreamActions.streamDelete({
                id: _.roomStreamDeleteCmd.value.streamId
              })
            ]),
            catchError((_) => [
              RoomStreamActions.roomStreamDeleteFailure({
                error: _.message
              })
            ])
          )
      )
    )
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this.actions$.pipe(
      ofType(RoomProducerActions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(this.actions$.pipe(ofType(RoomProducerActions.destroy)))
        )
      )
    );
  }

  private get accountRoom$(): Observable<{
    accountJwt: AnyAccountJwtVO;
    room: RoomEntity;
  }> {
    return combineLatest([
      this.accountJwtService.anyAccountJwtVO$.pipe(
        filter((_): _ is AnyAccountJwtVO => !!_)
      ),
      this.room$.pipe(filter((_): _ is RoomEntity => !!_))
    ]).pipe(
      take(1),
      map((_) => ({
        accountJwt: _[0],
        room: _[1]
      }))
    );
  }

  private publishStream<T extends LocalAnyConferenceStream>({
    stream,
    accountJwt,
    room
  }: {
    stream: T;
    accountJwt: AnyAccountJwtVO;
    room: RoomEntity;
  }) {
    const { streamId } = stream;

    return this.roomService.roomStreamToPublishCreate(
      new RoomStreamToPublishCreateRequestDTO({
        accountId: accountJwt.value.id,
        accountIdentity: accountJwt.value.identity,
        roomId: room.value.id,
        roomContextId: this.roomContextFacade.roomContextId,
        streamIsActive: false as StreamIsActive,
        streamId: streamId as StreamId,
        streamKinds: ["audio", "video"] as StreamKind[]
      })
    );
  }
}
